import React, {
  ReactElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Divider, Grid, Stack, Popover } from '@mui/material';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import type { SxProps, Theme } from '@mui/material';
import { Delete, Edit, MoreVert, PlayArrow } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import RestoreIcon from '@mui/icons-material/Restore';
import { useIntl } from 'react-intl';
import { useTemplateGeneralNormDocumentRouteSync } from 'components/common/graphicUploader/documentRouteSync';

import { MemoPreviewField } from '../../general/PreviewField';
import { MemoChecklistTreePreview } from '../ChecklistTreePreview';
import { MemoInspectionTemplateStatus } from '../Status';
import { InspectionTemplateStatusSize } from '../Status/types';
import { SingleTemplatePresentationalProps } from './types';

import { ProcessIcon } from 'components/common/ProcessIcon';
import { MemoButtonContained } from 'components/general/ButtonContained/ButtonContained';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import {
  ButtonTextIcon,
  SubheaderText,
  ButtonIcon,
  actionToolbarSx,
} from '../../general';
import { GeneralNormPreview } from 'components/inspection/GeneralNormPreview';
import { InspectionTranslationKeys } from 'intl/keys';
import { ChecklistTreeHeader } from 'components/common/checklistTree';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { DocumentToTemplateBinder } from 'shared/domain/document/documentBinder';
import { getDocumentScopeKey } from 'shared/domain/document/documentScope';
import { createInspectionTemplateGeneralNormDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';

import {
  UrlParamName,
  useAppUrlParams,
} from 'hooks/navigation/useAppUrlParams';
import useIsMobile from 'hooks/useIsMobile';
import { useProjectOrganizationId } from 'store/project';
import { getUserLabelOptionalByOrganization } from 'shared/domain/user/mapping/toView';
import { usePopover } from 'hooks/usePopover';
import { useAccordionsExpansion } from 'hooks/useAccordionsExpansion';
import { InspectionTemplateTranslationKeys } from 'intl/keys';

const START_INSPECTION_SX: SxProps<Theme> = {
  whiteSpace: 'nowrap',
};

// Below this *container* width the header actions collapse into a kebab menu.
// Window-based `useIsMobile` is not enough here: this view renders inside a
// narrow detail panel next to the template list, so we measure the container.
const ACTIONS_COLLAPSE_WIDTH = 800;

function SingleTemplatePresentational(
  props: SingleTemplatePresentationalProps
): ReactElement {
  const isMobile = useIsMobile();
  const intl = useIntl();
  const organizationId = useProjectOrganizationId();
  const urlProjectId = useAppUrlParams(UrlParamName.PROJECT_ID);

  const {
    template,
    checklist,
    canEditTemplate,
    editTemplate,
    canRemoveTemplate,
    removeTemplate,
    archiveTemplate,
    restoreTemplate,
    startInspection,
    canStartInspection,
    generalNormDocuments,
    loadingDocuments,
    sections,
  } = props;

  const checklistItemIds = useMemo(
    () => checklist.map((item) => item._id),
    [checklist]
  );
  const sectionIds = useMemo(
    () => sections.map((section) => section._id),
    [sections]
  );
  const expandableNodeIds = useMemo(
    () => Array.from(new Set([...sectionIds, ...checklistItemIds])),
    [sectionIds, checklistItemIds]
  );

  const {
    expandedIds,
    allExpanded,
    toggleExpandAll,
    onItemExpandedChange,
  } = useAccordionsExpansion(expandableNodeIds, {
    initialExpandedIds: sectionIds,
  });

  const urlPrefix = useMemo(() => {
    return urlProjectId ? `/project/${urlProjectId}` : '';
  }, [urlProjectId]);

  const internalUrlBuilder = useCallback(() => {
    return `${urlPrefix}/inspectionTemplate/${template._id}`;
  }, [template._id, urlPrefix]);

  const routeSync = useTemplateGeneralNormDocumentRouteSync(
    template._id,
    internalUrlBuilder
  );

  const uploaderConfig = useMemo(
    () => ({
      baseUrl: createInspectionTemplateGeneralNormDocumentsBaseUrl(
        template._id
      ),
      documentScopeKey: getDocumentScopeKey({ templateId: template._id }),
      previewOnly: true as const,
      internalEntityBaseUrl: routeSync.internalEntityBaseUrl,
      documentBinderFactory: () =>
        new DocumentToTemplateBinder({ templateId: template._id }),
      autoOpenCheck: routeSync.autoOpenCheck,
    }),
    [
      routeSync.autoOpenCheck,
      routeSync.internalEntityBaseUrl,
      template._id,
    ]
  );

  const GU = useGraphicUploader(
    generalNormDocuments,
    uploaderConfig,
    true
  );

  const edit = useCallback(() => {
    editTemplate(template);
  }, [template, editTemplate]);

  const remove = useCallback(() => {
    removeTemplate(template._id, template.summary);
  }, [template, removeTemplate]);

  const archive = useCallback(() => {
    archiveTemplate(template._id);
  }, [template._id, archiveTemplate]);

  const restore = useCallback(() => {
    restoreTemplate(template._id);
  }, [template._id, restoreTemplate]);

  const createInspection = useCallback(() => {
    startInspection(template);
  }, [template, startInspection]);

  const statusComponent = useMemo(
    () => (
      <MemoInspectionTemplateStatus
        status={template.status}
        size={InspectionTemplateStatusSize.default}
      />
    ),
    [template.status]
  );

  const { permission: editPermission, reason: editDisabledReason } =
    useMemo(() => canEditTemplate(template), [template, canEditTemplate]);

  const { permission: removalPermission, reason: removalDisabledReason } =
    canRemoveTemplate();

  const {
    permission: startInspectionPermission,
    reason: startInspectionDisabledReason,
  } = canStartInspection(template);

  const isEditButtonDisabled = !editPermission || template.deleted;
  const isArchiveButtonDisabled = !removalPermission || template.deleted;
  const isRestoreButtonDisabled = !removalPermission;

  const { anchorEl, popoverOpen, attachPopover, closePopover } =
    usePopover();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isContainerNarrow, setIsContainerNarrow] = useState(false);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const measure = (): void => {
      setIsContainerNarrow(element.clientWidth < ACTIONS_COLLAPSE_WIDTH);
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  const showKebab = isMobile || isContainerNarrow;

  const renderActionButtons = useCallback(
    (inMenu: boolean): ReactElement => {
      const editButton = (
        <MemoForbiddenTooltip
          visibleWhen={!editPermission}
          reason={editDisabledReason}
          inlined
        >
          <ButtonTextIcon
            disabled={isEditButtonDisabled}
            startIcon={Edit}
            onClick={edit}
          >
            {formatTranslation(intl, InspectionTemplateTranslationKeys.actionEdit)}
          </ButtonTextIcon>
        </MemoForbiddenTooltip>
      );

      const archiveRestoreButton = template.deleted ? (
        <MemoForbiddenTooltip
          visibleWhen={!removalPermission}
          reason={removalDisabledReason}
          inlined
        >
          <ButtonTextIcon
            disabled={isRestoreButtonDisabled}
            startIcon={RestoreIcon}
            onClick={restore}
          >
            {formatTranslation(intl, CommonTranslationKeys.restore)}
          </ButtonTextIcon>
        </MemoForbiddenTooltip>
      ) : (
        <MemoForbiddenTooltip
          visibleWhen={!removalPermission}
          reason={removalDisabledReason}
          inlined
        >
          <ButtonTextIcon
            disabled={isArchiveButtonDisabled}
            startIcon={ArchiveIcon}
            onClick={archive}
          >
            {formatTranslation(intl, CommonTranslationKeys.archive)}
          </ButtonTextIcon>
        </MemoForbiddenTooltip>
      );

      // CTA stays brand-yellow (contained primary); only secondary actions move
      // into the segmented toolbar.
      const startInspectionButton = (
        <MemoForbiddenTooltip
          visibleWhen={!startInspectionPermission}
          reason={startInspectionDisabledReason}
          inlined
        >
          {inMenu ? (
            <ButtonTextIcon
              disabled={!startInspectionPermission}
              startIcon={PlayArrow}
              onClick={createInspection}
            >
              {formatTranslation(intl, InspectionTemplateTranslationKeys.actionStartInspection)}
            </ButtonTextIcon>
          ) : (
            <MemoButtonContained
              disabled={!startInspectionPermission}
              onClick={createInspection}
              sx={START_INSPECTION_SX}
            >
              {formatTranslation(intl, InspectionTemplateTranslationKeys.actionStartInspection)}
            </MemoButtonContained>
          )}
        </MemoForbiddenTooltip>
      );

      if (inMenu) {
        return (
          <>
            {editButton}
            {archiveRestoreButton}
            {startInspectionButton}
          </>
        );
      }

      return (
        <>
          <Box sx={actionToolbarSx}>
            {editButton}
            {archiveRestoreButton}
          </Box>
          {startInspectionButton}
        </>
      );
    },
    [
      editPermission,
      editDisabledReason,
      isEditButtonDisabled,
      edit,
      template.deleted,
      removalPermission,
      removalDisabledReason,
      isArchiveButtonDisabled,
      isRestoreButtonDisabled,
      restore,
      archive,
      startInspectionPermission,
      startInspectionDisabledReason,
      createInspection,
      intl,
    ]
  );

  return (
    <Box ref={containerRef} sx={{ maxWidth: '100%', width: '100%', overflowY: 'auto' }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          p: '0 5%',
          my: 4,
          flexWrap: 'nowrap',
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <ProcessIcon
            color={template.renderableProcess.color}
            code={template.renderableProcess.code}
          />

          <SubheaderText
            sx={{
              fontSize: '1.125rem',
              margin: '16px 0',
            }}
          >
            {template.code}
          </SubheaderText>
        </Stack>

        <Stack
          direction='row'
          spacing={1.5}
          alignItems='center'
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        >
          {showKebab ? (
            <>
              <ButtonIcon
                onClick={attachPopover}
                size='small'
                sx={{
                  color: 'action.active',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <MoreVert />
              </ButtonIcon>

              <Popover
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Stack
                  direction='column'
                  sx={{
                    p: 1,
                    '& button': {
                      width: '100%',
                      justifyContent: 'flex-start',
                      px: 2.5,
                    },
                  }}
                >
                  {renderActionButtons(true)}
                </Stack>
              </Popover>
            </>
          ) : (
            renderActionButtons(false)
          )}
        </Stack>
      </Stack>

      <Divider sx={{ mb: 4 }} />

      <SubheaderText
        sx={{
          fontSize: '1.125rem',
          margin: '16px 0',
          padding: '0 5%',
        }}
      >
        {template.summary}
      </SubheaderText>

      <Divider sx={{ my: 4 }} />

      <Grid
        container
        columnSpacing={2}
        rowSpacing={2}
        sx={{
          padding: '0 5%',
        }}
      >
        <Grid item xs={12} sm={6}>
          <MemoPreviewField
            label={formatTranslation(intl, InspectionTemplateTranslationKeys.fieldLabelCreationDate)}
            value={template.createdAtDate.toLocaleDateString('en-GB')}
            dense={true}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MemoPreviewField
            label={formatTranslation(intl, InspectionTemplateTranslationKeys.fieldLabelStatus)}
            component={statusComponent}
            dense={true}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MemoPreviewField
            label={formatTranslation(intl, InspectionTemplateTranslationKeys.fieldLabelAuthor)}
            value={
              getUserLabelOptionalByOrganization(template.createdBy, organizationId) ||
              formatTranslation(intl, CommonTranslationKeys.userDeleted)
            }
            dense={true}
          />
        </Grid>
        {template.generalNormConfig?.isControlledPartyVisible && (
          <Grid item xs={12} sm={6}>
            <MemoPreviewField
              label={formatTranslation(intl, InspectionTranslationKeys.controlledParty)}
              value='-'
              dense={true}
            />
          </Grid>
        )}
        {template.generalNormConfig?.isContractNumberVisible && (
          <Grid item xs={12} sm={6}>
            <MemoPreviewField
              label={formatTranslation(intl, InspectionTranslationKeys.contractNumber)}
              value='-'
              dense={true}
            />
          </Grid>
        )}
        {template.generalNormConfig?.isWorkTypeVisible && (
          <Grid item xs={12} sm={6}>
            <MemoPreviewField
              label={formatTranslation(intl, InspectionTranslationKeys.columnLabelWorkTypes)}
              value='-'
              dense={true}
            />
          </Grid>
        )}
        {template.generalNormConfig?.isCommentVisible && (
          <Grid item xs={12} sm={6}>
            <MemoPreviewField
              label={formatTranslation(intl, InspectionTranslationKeys.comment)}
              value='-'
              dense={true}
            />
          </Grid>
        )}
        {template.generalNormConfig?.isInspectionResultVisible && (
          <Grid item xs={12} sm={6}>
            <MemoPreviewField
              label={formatTranslation(intl, InspectionTranslationKeys.columnLabelResult)}
              value='-'
              dense={true}
            />
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 4 }} />

      <SubheaderText
        sx={{
          fontSize: '1.125rem',
          margin: '16px 0',
          padding: '0 5%',
        }}
      >
        {formatTranslation(intl, InspectionTemplateTranslationKeys.headerGeneralAppliedStandard)}
      </SubheaderText>

      <Box
        sx={{
          padding: '0 5%',
        }}
      >
        <GeneralNormPreview
          generalNormDescription={template.generalNorm.description}
          GU={GU}
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ padding: '0 5%', marginBottom: '120px' }}>
        <ChecklistTreeHeader
          allExpanded={allExpanded}
          onToggleExpandAll={toggleExpandAll}
        />
        <MemoChecklistTreePreview
          templateId={template._id}
          checklist={checklist}
          sections={sections}
          expandedIds={expandedIds}
          onExpandedChange={onItemExpandedChange}
        />
      </Box>
    </Box>
  );
}

export const MemoSingleTemplatePresentational = React.memo(
  SingleTemplatePresentational
);
